import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Search, Filter, Calendar, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface MembershipFiltersState {
  search: string;
  type: string;
  status: string;
  paidFrom: string;
  paidTo: string;
  sortBy: 'client_name' | 'created_at';
  sortOrder: 'asc' | 'desc';
}

export const DEFAULT_FILTERS: MembershipFiltersState = {
  search: '',
  type: 'all',
  status: 'all',
  paidFrom: '',
  paidTo: '',
  sortBy: 'created_at',
  sortOrder: 'desc',
};

interface Props {
  filters: MembershipFiltersState;
  onChange: (filters: MembershipFiltersState) => void;
  plans: { name: string }[];
  activeFilterCount: number;
}

export function MembershipFilters({ filters, onChange, plans, activeFilterCount }: Props) {
  const [paidOpen, setPaidOpen] = useState(false);

  const set = (partial: Partial<MembershipFiltersState>) =>
    onChange({ ...filters, ...partial });

  const handleClear = () => onChange({ ...DEFAULT_FILTERS });

  const hasPaidFilter = filters.paidFrom || filters.paidTo;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar membresías..."
            value={filters.search}
            onChange={(e) => set({ search: e.target.value })}
            className="pl-10"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Select value={filters.type} onValueChange={(v) => set({ type: v })}>
            <SelectTrigger className="w-full sm:w-[160px]">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              {plans.map((plan) => (
                <SelectItem key={plan.name} value={plan.name}>
                  {plan.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filters.status} onValueChange={(v) => set({ status: v })}>
            <SelectTrigger className="w-full sm:w-[150px]">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="active">Activa</SelectItem>
              <SelectItem value="pending">Pendiente</SelectItem>
              <SelectItem value="expired">Vencida</SelectItem>
              <SelectItem value="inactive">Inactiva</SelectItem>
            </SelectContent>
          </Select>

          <Popover open={paidOpen} onOpenChange={setPaidOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'w-full sm:w-auto justify-start gap-2 font-normal',
                  hasPaidFilter && 'border-primary text-primary'
                )}
              >
                <Calendar className="h-4 w-4" />
                {hasPaidFilter ? (
                  <span className="truncate max-w-[140px]">
                    {filters.paidFrom && filters.paidTo
                      ? `${filters.paidFrom} → ${filters.paidTo}`
                      : filters.paidFrom
                        ? `Desde ${filters.paidFrom}`
                        : `Hasta ${filters.paidTo}`}
                  </span>
                ) : (
                  'Fecha de pago'
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-4" align="start">
              <div className="space-y-3">
                <p className="text-sm font-medium">Filtrar por fecha de pago</p>
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground">Desde</label>
                  <Input
                    type="date"
                    value={filters.paidFrom}
                    onChange={(e) => set({ paidFrom: e.target.value })}
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground">Hasta</label>
                  <Input
                    type="date"
                    value={filters.paidTo}
                    onChange={(e) => set({ paidTo: e.target.value })}
                    className="w-full"
                  />
                </div>
                {hasPaidFilter && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-xs"
                    onClick={() => {
                      set({ paidFrom: '', paidTo: '' });
                      setPaidOpen(false);
                    }}
                  >
                    <X className="h-3 w-3 mr-1" />
                    Limpiar fechas
                  </Button>
                )}
              </div>
            </PopoverContent>
          </Popover>

          <Select
            value={`${filters.sortBy}-${filters.sortOrder}`}
            onValueChange={(value) => {
              const [field, order] = value.split('-');
              set({ sortBy: field as any, sortOrder: order as any });
            }}
          >
            <SelectTrigger className="w-full sm:w-[170px]">
              <SelectValue placeholder="Ordenar por" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="client_name-asc">Cliente (A-Z)</SelectItem>
              <SelectItem value="client_name-desc">Cliente (Z-A)</SelectItem>
              <SelectItem value="created_at-desc">Recientes primero</SelectItem>
              <SelectItem value="created_at-asc">Antiguos primero</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {activeFilterCount > 0 && (
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">
            {activeFilterCount} filtro{activeFilterCount !== 1 ? 's' : ''} activo{activeFilterCount !== 1 ? 's' : ''}
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClear}
            className="h-7 text-xs text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3 mr-1" />
            Limpiar filtros
          </Button>
        </div>
      )}
    </div>
  );
}
